import * as THREE from 'three';
import { WebCADGlobal } from "../../..";
import * as marietta from '../../../../';
import { ThreeView } from '../../../../view3d/webcad';
import { IntersectItem } from '../../../../view3d/webcad/util';
import { PinDialogUtil } from '../../../../view3d/webcad/util/PinDialogUtil';
import { ExtrasType } from "../../../../view3d/webcad/WebCadDefines";
import { ItemInfoCollection } from "../../../collection";
import { IntersectItemType } from '../../../collection/ItemInfoCollection';
import { MonicaObject } from '../../../item';
import { WizardManager } from './WizardManager';

export interface intersectItemGUID {
    pinGuid: string,
    hitGuid: string,
    type:    ExtrasType
};

export class WizardTool {

    private pinInfo: ItemInfoCollection = null;

    constructor() {}

    /** ピンに紐づくアイテムのguidをセット */
    public setIntersectUID(uidList: intersectItemGUID[]): void {
        const pinList = new WizardManager().getTargetPin();
        uidList.forEach((uidObj) => {
            const targetPin = pinList.find((pin) => pin.itemInfo.guid === uidObj.pinGuid);
            if (!targetPin) return;

            targetPin.itemInfo.intersectItem = {
                uid: uidObj.hitGuid,
                type: uidObj.type
            };
        });
    }

    /** アイテム自動紐づけ */
    public autoIntersectItem(): void {
        try {
            const wizardTool = new WizardTool();
            const hitList = wizardTool.hit();
            wizardTool.setIntersectUID(hitList);
            WebCADGlobal.dimensionMenu.changeMode2D();
            WebCADGlobal.webcad2Dmanager.saveToHistory();
        } catch (err) {
            WebCADGlobal.dimensionMenu.changeMode2D();
            marietta.webcad.WebCADGlobal.showAlert('3Dに遷移できません。<br>図面を編集して再度 ヘッダー > 編集ボタン > ピンとアイテムの自動紐づけボタンから実行してください。');
        }
    }

    /** ウィザード開始 */
    public wizardMode(): void {
        this.autoIntersectItem();
        const param = {
            view_mode: false
        };
        marietta.webcad.view.dialog.WizardPinIntersect.pinList = new WizardManager().getTargetPin();
        WebCADGlobal.showDialog(marietta.webcad.view.dialog.WizardPinIntersect, true, false, false, false, param);
    }

    /** 【ウィザード中】ピンに紐づけるアイテムの変更 */
    public wizardChangeMode(iteminfo: ItemInfoCollection) {
        this.pinInfo = iteminfo;
        if (!iteminfo) {
            console.warn('ピンがありません');
            return;
        }
        this.initCameraSettingChangeMode(iteminfo);
        this.initChangeMode();

        ThreeView.get().container.addEventListener('pointerup', this.confirmWizardDialog);
    }

    /** ピンに紐づけるアイテムの変更 */
    public changeMode(iteminfo: ItemInfoCollection): void {
        this.pinInfo = iteminfo;
        if (!iteminfo) {
            console.warn('ピンがありません');
            return;
        }
        this.initCameraSettingChangeMode(iteminfo);
        this.initChangeMode();

        ThreeView.get().container.addEventListener('pointerup', this.confirmDialog);
    }

    /** ピンに紐づけるアイテムの変更 3Dver */
    public changeMode3D(pininfo: ItemInfoCollection) {
        this.pinInfo = pininfo;
        this.initChangeMode();

        ThreeView.get().container.addEventListener('pointerup', this.confirm3DDialog);
    }

    /** 紐づけアイテム変更時のカメラ位置 */
    private initCameraSettingChangeMode(iteminfo: ItemInfoCollection) {
        ThreeView.get().menus.walkthroughCamera();

        const obj = this.getTargetPin(iteminfo.guid);
        ThreeView.get().wcItemmgr.setCameraPosition(this.getPosition(obj), this.getDirection(obj));
        if (iteminfo.floor === 2) (<HTMLInputElement>document.querySelector('#show2F_3dwt')).click();
        if (iteminfo.floor === 3) (<HTMLInputElement>document.querySelector('#show3F_3dwt')).click();
    }

    /** ピンに紐づけるアイテム変更の開始 */
    private initChangeMode() {
        ThreeView.get().selectManager.invalidate = true;   // 選択

        // 右上に「紐づけるアイテムを選択してください」
        document.getElementById('pinWizardInfo').style.display = 'block';

        // メニューは選択できないようにする
        this.toggleLimitMenu(false);
    }

    private confirmWizardDialog = (e) => {
        if (!ThreeView.get().ItemDragManager.enabled) return;

        const intersectItem = this.intersectItem(e);

        const intersectItemInfo = PinDialogUtil.getCurrentItem(intersectItem.uid);
        const iteminfo = intersectItemInfo instanceof MonicaObject ? intersectItemInfo.itemInfo : intersectItemInfo?.parentObj?.itemInfo;
        const roomName = this.selectedRoomName(iteminfo, intersectItem);
        const param = {
            isWizard:  true,
            isTargetItem: true,
            itemName:  PinDialogUtil.getItemName(iteminfo, intersectItem),
            roomName,
        };
        const dialog = marietta.webcad.WebCADGlobal.showDialog(marietta.webcad.view.dialog.PinItemChangeDialog, true, true, false, false, param);
        dialog.callBack = () => {
            if (intersectItem) {
                this.applyIntersectItem(intersectItem, roomName);
            }

            const callback = () => {
                setTimeout(() => {
                    marietta.webcad.WebCADGlobal.hideBusy();
                    if (marietta.webcad.view.dialog.WizardPinIntersect.pinList.length == marietta.webcad.view.dialog.WizardPinIntersect.currentIndex) {
                        return;
                    }
                    WebCADGlobal.showDialog(marietta.webcad.view.dialog.WizardPinIntersect, true, false, false, false, { view_mode: false });
                }, 500);
            }

            marietta.webcad.WebCADGlobal.showBusy('アイテムを紐づけ中...', callback);
            this.resetChangeMode();
            WebCADGlobal.dimensionMenu.changeMode2D();
            ThreeView.get().container.removeEventListener('pointerup', this.confirmWizardDialog);
        }
    }

    private confirmDialog = (e) => {
        if (!ThreeView.get().ItemDragManager.enabled) return;

        const intersectItem = this.intersectItem(e);

        const intersectItemInfo = PinDialogUtil.getCurrentItem(intersectItem.uid);
        const iteminfo = intersectItemInfo instanceof MonicaObject ? intersectItemInfo.itemInfo : intersectItemInfo?.parentObj?.itemInfo;
        const roomName = this.selectedRoomName(iteminfo, intersectItem);
        const param = {
            isWizard:  false,
            isTargetItem: true,
            itemName:  PinDialogUtil.getItemName(iteminfo, intersectItem),
            roomName,
        };
        const dialog = marietta.webcad.WebCADGlobal.showDialog(marietta.webcad.view.dialog.PinItemChangeDialog, true, true, false, false, param);
        dialog.callBack = () => {
            if (intersectItem) {
                this.applyIntersectItem(intersectItem, roomName);
                marietta.webcad.WebCADGlobal.showBusy('アイテムを紐づけ中...', () => setTimeout(() => marietta.webcad.WebCADGlobal.hideBusy(), 500));
            }
            this.resetChangeMode();
            WebCADGlobal.dimensionMenu.changeMode2D();
            ThreeView.get().container.removeEventListener('pointerup', this.confirmDialog);

            marietta.webcad.WebCADGlobal.quotationItemList.requestRebuild();
        }
    }

    private confirm3DDialog = (e) => {
        if (!ThreeView.get().ItemDragManager.enabled) return;

        const intersectItem = this.intersectItem(e);

        const intersectItemInfo = PinDialogUtil.getCurrentItem(intersectItem.uid);
        const iteminfo = intersectItemInfo instanceof MonicaObject ? intersectItemInfo.itemInfo : intersectItemInfo?.parentObj?.itemInfo;
        const roomName = this.selectedRoomName(iteminfo, intersectItem);
        const param = {
            isTargetItem: true,
            itemName:  PinDialogUtil.getItemName(iteminfo, intersectItem),
            roomName,
        };
        const dialog = marietta.webcad.WebCADGlobal.showDialog(marietta.webcad.view.dialog.PinItemChangeDialog, true, true, false, false, param);
        dialog.callBack = () => {
            if (intersectItem) {
                this.applyIntersectItem(intersectItem, roomName);
                marietta.webcad.WebCADGlobal.showBusy('アイテムを紐づけ中...', () => setTimeout(() => marietta.webcad.WebCADGlobal.hideBusy(), 500));
            }
            this.resetChangeMode();
            ThreeView.get().container.removeEventListener('pointerup', this.confirm3DDialog);

            marietta.webcad.WebCADGlobal.quotationItemList.requestRebuild();
        }
    }

    /**
     * 紐づけを確定し、決めた部屋名と確定時刻をピンに記録する。
     * 確定時刻は、同じアイテムに複数のピンが紐づいたときに最後の紐づけを正とするために使う。
     */
    private applyIntersectItem(intersectItem: IntersectItemType, roomName: string): void {
        // 部屋名を決められなかった紐づけに時刻を残すと、古い部屋名が最新扱いになるため除く
        const decidesRoomName: boolean = !!(intersectItem.uid && this.pinInfo?.auxiliaryData?.auxiliaryDatum && roomName);
        this.pinInfo.intersectItem = decidesRoomName ? { ...intersectItem, linkedAt: Date.now() } : { ...intersectItem };
        marietta.webcad.WebCADGlobal.webcad2Dmanager.saveToHistory();

        if (decidesRoomName) {
            this.pinInfo.auxiliaryData.auxiliaryDatum.room_name = roomName;
        }
    }

    /**
     * 紐づけ先として表示・保存する部屋名。
     * 建具は所属部屋が一意に定まらないため、選択した時点の3Dの現在地にある部屋を優先する。
     */
    private selectedRoomName(iteminfo: ItemInfoCollection, intersectItem: IntersectItemType): string {
        const itemRoomName = () => PinDialogUtil.getItemRoomName(iteminfo, intersectItem, this.pinInfo, false);
        if (!PinDialogUtil.isFittingTargetPinType(intersectItem)) return itemRoomName();

        return PinDialogUtil.getRoomNameAtCameraPosition() ?? itemRoomName();
    }

    private resetChangeMode() {
        if (document.getElementById('pinWizardInfo')) {
            document.getElementById('pinWizardInfo').style.display = 'none';
        }
        this.toggleLimitMenu(true);
        ThreeView.get().selectManager.invalidate = false;   // 選択
    }

    /** 衝突判定 */
    public hit(): intersectItemGUID[] {
        const result: intersectItemGUID[] = [];
        const targetItems = this.getTargetItems(ThreeView.get().wcScene.me);
        ThreeView.get().wcItemmgr.furnitureObjects.forEach((obj: THREE.Group) => {
            if (obj.userData?.modelInfo?.type != 'pin') return;
            if (result.some((res) => res.pinGuid === obj.userData?.modelInfo?.uuid)) return;

            const intersectItem = new IntersectItem();
            intersectItem.setTargetItems(targetItems);

            const raycaster = new THREE.Raycaster(this.getPosition(obj), this.getDirection(obj));
            const targetItem = intersectItem.targetItem(raycaster);
            // this.debug(false, origin, direction);
            console.log(targetItem);

            result.push({
                pinGuid: obj.userData?.modelInfo?.uuid,
                hitGuid: targetItem?.userData?.guid ?? targetItem?.userData?.modelInfo?.uuid,
                type:    targetItem?.userData?.type ?? targetItem?.userData?.typeExtras
            });
        });

        return result;
    }

    /** メニューを選択できないよう制限をかける */
    private toggleLimitMenu(enabled: boolean) {
        (<HTMLElement>document.getElementById('itemChangeMenuWrap')).style.display = enabled ? 'block' : 'none';

        const limitMenu = (element: HTMLElement) => {
            const className = 'disabled-button3d';
            enabled ? element.classList.remove(className) : element.classList.add(className);
        }
        limitMenu(document.getElementById('btnMesureParent'));
        limitMenu(document.getElementById('changeCamToggleCol'));
        limitMenu(document.getElementById('headerMenuResetGrade'));
        limitMenu(document.getElementById('headerMenuSetting'));
        limitMenu(document.getElementById('changeDimensionBtns'));
        limitMenu(document.getElementById('btnOrbitCamera'));

        const closeSearchLimitMenu = (id: string) => {
            const targetElements = document.querySelectorAll(`#${id}`);
            const targetElement = [...targetElements].find((ele) => ele.closest('.for3D'));
            if (!targetElement) return;

            limitMenu(targetElement as HTMLElement);
        }
        closeSearchLimitMenu('headerMenuFile');
        closeSearchLimitMenu('btnCalculator');
    }

    /** ピンに紐づけるアイテムの変更 */
    private intersectItem(e: MouseEvent): {uid: string, type: ExtrasType} {
        const targetItems = this.getTargetItems(ThreeView.get().wcScene.me);

        const initRayCaster = new THREE.Raycaster();
        initRayCaster.setFromCamera(this.pointer(e), ThreeView.get().wcCameras.camera.me);

        const intersectItem = new IntersectItem();
        intersectItem.setTargetItems(targetItems);
        const targetItem = intersectItem.targetItem(initRayCaster);

        return {
            uid: targetItem?.userData?.guid ?? targetItem?.userData?.modelInfo?.uuid,
            type: targetItem?.userData?.type ?? this.fittingTypeFromModelInfo(targetItem)
        };
    }

    /**
     * 建具モデルの3Dオブジェクトは userData.type を持たず、種別は userData.modelInfo.typeExtras にのみ格納されている。
     * 建具（DOOR/WIN/SHUTTER）に限り typeExtras から種別を補完する（建具以外は従来どおり null を返し挙動を変えない）。
     */
    private fittingTypeFromModelInfo(targetItem: THREE.Object3D): ExtrasType {
        const typeExtras: ExtrasType = targetItem?.userData?.modelInfo?.typeExtras ?? null;
        return PinDialogUtil.isFittingTargetPinType({ uid: '', type: typeExtras }) ? typeExtras : null;
    }

    private isMeshTargetType(mesh: THREE.Mesh): boolean {
        return [
            ExtrasType.KABE, ExtrasType.KOSI, ExtrasType.HABAKI, ExtrasType.FLOOR, ExtrasType.CEILING
        ].some((type) => mesh.userData?.type === type);
    }

    private isObjTargetType(obj: THREE.Object3D): boolean {
        return [
            ExtrasType.DOOR, ExtrasType.WIN, ExtrasType.SHUTTER, ExtrasType.EXT, ExtrasType.EQU, ExtrasType.FUR
        ].some((type) => obj.userData?.modelInfo?.typeExtras === type);
    }

    /** 衝突判定に含めるアイテムを取得 */
    private getTargetItems(scene: THREE.Scene): THREE.Object3D[] {
        const group: THREE.Object3D[] = [];
        scene.traverse((object: THREE.Object3D) => {
            if (this.isObjTargetType(object)) {
                group.push(object);
            }

            const mesh = <THREE.Mesh>object;
            if (!mesh.isMesh) return;
            if(!this.isMeshTargetType(mesh)) return;

            group.push(mesh);
        });
        return group;
    }

    private pointer(e: MouseEvent) {
        const element = e.currentTarget as HTMLElement;
        // canvas要素上のXY座標
        // canvas要素の幅・高さ
          const rect = element.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const w = rect.width;
          const h = rect.height;

        // -1〜+1の範囲で現在のマウス座標を登録する
        const pointer = new THREE.Vector2();
        pointer.x = (x / w) * 2 - 1;
        pointer.y = -(y / h) * 2 + 1;

        return pointer;
    }

    private getTargetPin(guid: string): THREE.Object3D {
        return ThreeView.get().wcItemmgr.furnitureObjects.find((obj: THREE.Group) => {
            if (obj.userData?.modelInfo?.type != 'pin') return;
            return obj.userData?.modelInfo?.uuid === guid;
        });
    }

    private getPosition(obj: THREE.Object3D): THREE.Vector3 {
        const modelInfo = obj.userData.modelInfo;
        return new THREE.Vector3(modelInfo.translateX, modelInfo.translateY, modelInfo.translateZ);
    }

    private getDirection(obj: THREE.Object3D): THREE.Vector3 {
        obj.updateMatrixWorld(true);
        const direction = new THREE.Vector3();
        const matrixWorld = obj.matrixWorld;
        direction.set(matrixWorld.elements[8], matrixWorld.elements[9], matrixWorld.elements[10]);
        direction.normalize();
        return direction;
    }

    private debug(debug: boolean, origin: THREE.Vector3, direction: THREE.Vector3) {
        if (debug) {
            const length = 1;
            const hex = 0xffff00;
            const arrowHelper = new THREE.ArrowHelper( direction, origin, length, hex );
            ThreeView.get().wcScene.me.add(arrowHelper);
        }
    }
}
